import { useEffect, useRef, useState } from 'react';

/**
 * Simple in-memory cache for search suggestions.
 * @type {Map<string, {at:number, data:Array}>>}
 */
const memoryCache = new Map();
const TTL = 5 * 60 * 1000;

/**
 * Hook to perform debounced unified search.
 * @param {(q: string, limit?: number) => Promise<Array>} fetcher
 * @param {{limit?: number, debounce?: number}} [opts]
 */
export const useUnifiedSearch = (fetcher, opts = {}) => {
  const { limit = 5, debounce = 250, invalidateKey } = opts;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const t = useRef(undefined);
  const lastInvalidate = useRef(invalidateKey);

  const doFetch = async (q) => {
    const key = `${q}::${limit}`;
    const now = Date.now();
    const cached = memoryCache.get(key);
    if (cached && now - cached.at < TTL) {
      setResults(cached.data);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetcher(q, limit);
      if (Array.isArray(data) && data.length > 0) {
        memoryCache.set(key, { at: now, data });
      }
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (t.current !== undefined) window.clearTimeout(t.current);
    if (!query?.trim()) {
      setResults([]);
      return;
    }
    t.current = window.setTimeout(() => doFetch(query.trim()), debounce);
  }, [query, limit, debounce]);

  useEffect(() => {
    if (invalidateKey === lastInvalidate.current) return;
    lastInvalidate.current = invalidateKey;
    memoryCache.clear();
    if (query?.trim()) {
      doFetch(query.trim());
    }
  }, [invalidateKey, query, limit]);

  return { query, setQuery, results, loading, error };
};
